import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { XCircle, AlertTriangle, RefreshCw, Mail } from 'lucide-react';
import { getDonation } from '@/lib/api/donations';
import { formatCurrency } from '@/lib/utils/currency';
import { Card, Button, Alert } from '@/components/ui';
import type { Currency } from '@/types/donation';

function FailurePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const donationId = searchParams.get('id');
  const errorCode = searchParams.get('error');

  // Fetch donation details if ID is available
  const { data } = useQuery({
    queryKey: ['donation', donationId],
    queryFn: () => {
      if (!donationId) {
        throw new Error('Donation ID is required');
      }
      return getDonation(donationId);
    },
    enabled: !!donationId,
    retry: 1,
  });

  const donation = data?.data;

  // Error messages based on error codes
  const getErrorMessage = () => {
    switch (errorCode) {
      case 'no_donation_id':
        return 'No donation reference was found. The payment may not have been initiated properly.';
      case 'insufficient_funds':
        return 'Your payment was declined due to insufficient funds.';
      case 'card_declined':
        return 'Your card was declined by your bank.';
      case 'expired_card':
        return 'Your card has expired.';
      case 'invalid_card':
        return 'The card details provided were invalid.';
      case 'authentication_failed':
        return 'Card authentication (3D Secure) failed.';
      case 'network_error':
        return 'A network error occurred while processing your payment.';
      default:
        return 'Unfortunately, your payment could not be processed.';
    }
  };

  // Common reasons for failure
  const commonReasons = [
    'Insufficient funds in your account',
    'Card expired or invalid card details',
    'Payment declined by your bank',
    'Network connectivity issues',
    'Transaction limit exceeded',
  ];

  return (
    <div className="container mx-auto px-4 py-12 min-h-screen">
      <div className="max-w-2xl mx-auto">
        <Card className="text-center">
          {/* Error Icon */}
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full">
              <XCircle className="w-12 h-12 text-red-600" />
            </div>
          </div>

          {/* Error Message */}
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Payment Failed
          </h1>
          <p className="text-lg text-gray-600 mb-6">
            {getErrorMessage()}
          </p>

          {/* Donation Details (if available) */}
          {donation && (
            <div className="bg-gray-50 rounded-lg p-6 mb-6 text-left">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Attempted Donation</h2>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Amount:</span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(donation.amount, donation.currency as Currency)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className="px-2 py-1 bg-red-100 text-red-800 text-sm rounded">
                    Failed
                  </span>
                </div>
                {donationId && (
                  <div className="flex justify-between items-start">
                    <span className="text-gray-600">Reference:</span>
                    <span className="font-mono text-xs text-gray-900 break-all text-right">
                      {donationId}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Common Reasons */}
          <Alert variant="warning" className="mb-6 text-left">
            <div>
              <div className="font-semibold mb-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Common reasons for payment failures:
              </div>
              <ul className="list-disc list-inside space-y-1 text-sm">
                {commonReasons.map((reason, index) => (
                  <li key={index}>{reason}</li>
                ))}
              </ul>
            </div>
          </Alert>

          {/* What to do next */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
            <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
              <Mail className="w-4 h-4" />
              What to do next:
            </h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Check your card details and try again</li>
              <li>• Contact your bank to ensure the transaction isn't blocked</li>
              <li>• Try using a different payment method or card</li>
              <li>• Contact our support team if the issue persists</li>
            </ul>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <Button
              onClick={() => navigate('/')}
              variant="primary"
              className="w-full"
              size="lg"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </Button>
            <Button
              onClick={() => {
                window.location.href = 'mailto:support@example.com' +
                  (donationId ? `?subject=Payment Failed - ${donationId}` : '?subject=Payment Failed');
              }}
              variant="outline"
              className="w-full"
            >
              <Mail className="w-4 h-4 mr-2" />
              Contact Support
            </Button>
          </div>

          {/* Reference Number */}
          {donationId && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                Please quote this reference when contacting support:
              </p>
              <p className="font-mono text-sm font-semibold text-gray-700 mt-1">
                {donationId}
              </p>
            </div>
          )}

          {/* Reassurance */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              💡 <strong>Note:</strong> No charges were made to your account. Your payment was not processed.
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}

export default FailurePage;
